export type CookieYesConsentState = {
  raw: string | null;
  hasExplicitAction: boolean;
  consentValue: string | null;
  analyticsValue: string | null;
};

export type MarketingAnalyticsMode = "standard" | "cookieless_reject";

function decodeCookieValue(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function findCookie(cookieName: string, cookieSource: string) {
  const prefix = `${cookieName}=`;

  for (const segment of cookieSource.split(";")) {
    const trimmed = segment.trim();

    if (trimmed.startsWith(prefix)) {
      return trimmed.slice(prefix.length);
    }
  }

  return null;
}

function parseCookieYesConsent(raw: string | null): CookieYesConsentState {
  if (!raw) {
    return {
      raw: null,
      hasExplicitAction: false,
      consentValue: null,
      analyticsValue: null,
    };
  }

  const pairs = Object.fromEntries(
    raw
      .split(",")
      .map((segment) => {
        const separatorIndex = segment.indexOf(":");

        if (separatorIndex === -1) {
          return [segment.trim(), ""];
        }

        return [
          segment.slice(0, separatorIndex).trim(),
          segment.slice(separatorIndex + 1).trim(),
        ];
      })
      .filter(([key]) => key !== ""),
  );

  return {
    raw,
    hasExplicitAction: (pairs.action ?? "").trim() !== "",
    consentValue: (pairs.consent ?? "").trim().toLowerCase() || null,
    analyticsValue: (pairs.analytics ?? "").trim().toLowerCase() || null,
  };
}

export function readCookieYesConsentState(): CookieYesConsentState {
  if (typeof document === "undefined") {
    return {
      raw: null,
      hasExplicitAction: false,
      consentValue: null,
      analyticsValue: null,
    };
  }

  const cookieValue = findCookie("cookieyes-consent", document.cookie);

  return parseCookieYesConsent(cookieValue ? decodeCookieValue(cookieValue) : null);
}

export function isMarketingAnalyticsAllowed(consent: CookieYesConsentState) {
  return resolveMarketingAnalyticsMode(consent) === "standard";
}

export function resolveMarketingAnalyticsMode(
  consent: CookieYesConsentState,
): MarketingAnalyticsMode {
  if (consent.hasExplicitAction && consent.analyticsValue === "no") {
    return "cookieless_reject";
  }

  return "standard";
}

export function sameCookieYesConsentState(
  left: CookieYesConsentState | null,
  right: CookieYesConsentState,
) {
  return (
    left?.raw === right.raw &&
    left?.hasExplicitAction === right.hasExplicitAction &&
    left?.consentValue === right.consentValue &&
    left?.analyticsValue === right.analyticsValue
  );
}
